import React, { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Modal,
  Box,
  Typography,
  CardMedia,
  IconButton,
  CircularProgress,
  Divider,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  getCombinedList,
  normalizeCountySlug,
} from "../../functions/availablePermitForms";
import {
  addAvailablePermitForms,
  setPrevCityCounty,
  updatePermitPackage,
} from "../../store/actions/dataActions";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import DocumentAddedDialog from "./DocumentAddedDialog";

function AddSinglePermitDocumentModal({ open, setOpen }) {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const availablePermitForms = useSelector(
    (state) => state.nocData?.available_permit_forms,
  );
  const prevCityCounty = useSelector((state) => state.nocData.prevCityCounty);

  const dispatch = useDispatch();
  const [availableForms, setAvailableForms] = useState([]);
  const [newSelectedForms, setNewSelectedForms] = useState([]);
  const [addNewDocLoading, setAddNewDocLoading] = useState(false);
  const [countyLoading, setCountyLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activePreviewDoc, setActivePreviewDoc] = useState(null);
  const activePreviewCover =
    activePreviewDoc?.cover_img || activePreviewDoc?.cover_image_url || "";
  const activePreviewCoverUrl = activePreviewCover.startsWith("http")
    ? activePreviewCover
    : `${process.env.REACT_APP_URL || ""}${activePreviewCover}`;

  const getCountyJurisdictionId = useCallback(() => {
    if (selectedRow?.county_jurisdiction_id) {
      return selectedRow.county_jurisdiction_id;
    }
    if (selectedRow?.jurisdiction_id) {
      const parts = selectedRow.jurisdiction_id.split("_");
      if (parts.length >= 4) {
        return parts.slice(0, 4).join("_");
      }
    }

    const countyName = selectedRow?.county;
    if (!countyName) return "";

    const normalized = normalizeCountySlug(countyName);

    const countyCards = currentUser?.displays?.county_cards?.jur_us_fl || [];
    const matchedCard = countyCards.find((card) => {
      return (
        card?.id === `jur_us_fl_${normalized}` ||
        card?.slug === normalized ||
        card?.name?.toLowerCase() === countyName?.toLowerCase() ||
        normalizeCountySlug(card?.name || "") === normalized
      );
    });

    if (matchedCard?.id) {
      return matchedCard.id;
    }

    const selectedCountiesNOC = currentUser?.user?.selectedCountiesNOC || [];
    const matchedSelected = selectedCountiesNOC.find((c) => {
      return (
        c?.jurisdiction_id === `jur_us_fl_${normalized}` ||
        c?.name?.toLowerCase() === countyName?.toLowerCase() ||
        normalizeCountySlug(c?.name || "") === normalized
      );
    });

    if (matchedSelected?.jurisdiction_id) {
      return matchedSelected.jurisdiction_id;
    }

    return normalized ? `jur_us_fl_${normalized}` : "";
  }, [
    currentUser,
    selectedRow?.county,
    selectedRow?.county_jurisdiction_id,
    selectedRow?.jurisdiction_id,
  ]);

  const handleAddingDoc = async () => {
    try {
      setAddNewDocLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/add-new-permit-doc`,
        {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
          newSelectedForms: newSelectedForms,
        },
        {
          withCredentials: true,
        },
      );
      if (resp?.data?.success) {
        await dispatch(updatePermitPackage(resp?.data?.data));
        setDialogOpen(true);
      }
    } catch (error) {
    } finally {
      setAddNewDocLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !selectedRow?.county) {
      return;
    }

    const jurisdictionId = getCountyJurisdictionId();
    if (!jurisdictionId) {
      console.error(
        "No valid jurisdiction_id found for county:",
        selectedRow?.county,
      );
      return;
    }

    const loadPermitForms = async () => {
      try {
        setCountyLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/data/get-county-permit`,
          { currentSession: currentSession, jurisdiction_id: jurisdictionId },
          {
            withCredentials: true,
          },
        );
        await dispatch(addAvailablePermitForms(resp?.data?.forms));
        await dispatch(
          setPrevCityCounty({
            city: selectedRow?.city,
            county: selectedRow?.county,
          }),
        );

        return resp?.data?.forms;
      } catch (e) {
        // Handle error if needed
        console.error("Failed to load permit forms", e);
        return [];
      } finally {
        setCountyLoading(false);
      }
    };

    const processPermitForms = async () => {
      const loadedForms =
        !availablePermitForms ||
        availablePermitForms?.length === 0 ||
        prevCityCounty?.county === "" ||
        prevCityCounty?.county !== selectedRow?.county ||
        prevCityCounty?.city !== selectedRow?.city
          ? await loadPermitForms()
          : availablePermitForms;

      const oldForms =
        selectedRow?.selected_documents?.map((selected_doc) => {
          return selected_doc.name;
        }) || [];

      const forms = getCombinedList(
        loadedForms,
        selectedRow?.county,
        selectedRow?.city,
        newSelectedForms,
        setNewSelectedForms,
        oldForms,
        null,
      );
      setAvailableForms(forms?.permits);
    };

    processPermitForms();
  }, [
    open,
    selectedRow?.city,
    selectedRow?.county,
    availablePermitForms,
    currentSession,
    dispatch,
    getCountyJurisdictionId,
    newSelectedForms,
    prevCityCounty?.city,
    prevCityCounty?.county,
    selectedRow?.selected_documents,
  ]);

  useEffect(() => {
    if (!availableForms?.length) {
      setActivePreviewDoc(null);
      return;
    }

    const activeDocStillExists = availableForms.find(
      (doc) => doc?.name === activePreviewDoc?.name,
    );

    if (!activeDocStillExists) {
      setActivePreviewDoc(null);
    }

    if (!activePreviewDoc && newSelectedForms?.length > 0) {
      const nextSelected = availableForms.find(
        (doc) => doc?.name === newSelectedForms[0],
      );
      if (nextSelected) {
        setActivePreviewDoc(nextSelected);
      }
    }
  }, [availableForms, activePreviewDoc, newSelectedForms]);

  const handleCardClick = useCallback((item) => {
    setNewSelectedForms((prevSelectedForms) => {
      const isAlreadySelected = prevSelectedForms.includes(item?.name);
      const updatedSelectedForms = isAlreadySelected
        ? prevSelectedForms.filter((formName) => formName !== item?.name)
        : [...prevSelectedForms, item?.name];

      return updatedSelectedForms;
    });
  }, []);

  const handleListItemClick = useCallback(
    (item) => {
      if (item?.name === "noc") {
        return;
      }
      setActivePreviewDoc(item);
      handleCardClick(item);
    },
    [handleCardClick],
  );

  return (
    <Modal open={open} sx={{ overflow: "auto" }}>
      <Box
        sx={{
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          height: "92vh",
          width: "95vw",
          maxWidth: "80rem",
          bgcolor: "background.paper",
          borderRadius: "18px",
          boxShadow: 24,
          px: 4,
          py: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 1,
            position: "absolute",
            width: "30rem",
            height: "8rem",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)", // Center the box horizontally and vertically
            zIndex: 9999,
            pointerEvents: dialogOpen ? "auto" : "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <DocumentAddedDialog
              open={dialogOpen}
              setOpen={setDialogOpen}
              closeParent={setOpen}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              id="modal-modal-title"
              variant="h4"
              component="h2"
              color="primary"
            >
              Select Additional Documents
            </Typography>
            <Typography
              color="primary"
              id="modal-modal-description"
              sx={{ fontWeight: 600, mt: 0.5 }}
            >
              From the list of available documents, you can add more documents
              to your permit package.
            </Typography>
          </Box>
          <IconButton
            disabled={addNewDocLoading}
            onClick={() => {
              setOpen(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mt: 2 }} />

        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 3,
            mt: 2,
            minHeight: 0,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              borderRadius: "16px",
              p: 3,
              border: "1px solid rgba(25, 118, 210, 0.15)",
            }}
          >
            <Typography variant="h6" color="primary" fontWeight={700}>
              Document Review
            </Typography>
            <Divider sx={{ my: 2 }} />

            {countyLoading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress color="warning" size="5rem" />
              </Box>
            ) : activePreviewDoc ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minHeight: 0,
                }}
              >
                <Typography variant="h5" color="primary" fontWeight={700}>
                  {activePreviewDoc?.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Form key: {activePreviewDoc?.name}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "16px",
                    border: "1px dashed rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(25, 118, 210, 0.03)",
                    p: 2,
                    minHeight: 0,
                  }}
                >
                  <CardMedia
                    component="img"
                    image={activePreviewCoverUrl}
                    alt={activePreviewDoc?.label}
                    sx={{
                      maxHeight: "52vh",
                      width: "auto",
                      maxWidth: "100%",
                      objectFit: "contain",
                      borderRadius: "12px",
                      boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.15)",
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Select a document to preview
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper
            elevation={3}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              borderRadius: "16px",
              p: 3,
              border: "1px solid rgba(25, 118, 210, 0.15)",
            }}
          >
            <Typography variant="h6" color="primary" fontWeight={700}>
              Available Documents
            </Typography>
            <Divider sx={{ my: 2 }} />

            {countyLoading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress color="warning" size="4rem" />
              </Box>
            ) : availableForms?.length > 0 ? (
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <List sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {availableForms?.map((item) => {
                    const isSelected = newSelectedForms.includes(item?.name);
                    const isActive = activePreviewDoc?.name === item?.name;
                    const isDisabled = item?.name === "noc";

                    return (
                      <ListItemButton
                        key={item?.name}
                        selected={isActive}
                        disabled={isDisabled}
                        onClick={() => handleListItemClick(item)}
                        sx={{
                          borderRadius: "12px",
                          border: "1px solid",
                          borderColor: isActive
                            ? "primary.main"
                            : "rgba(0,0,0,0.08)",
                          alignItems: "flex-start",
                          gap: 2,
                          py: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <ListItemText
                            primary={
                              <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                color={
                                  isDisabled ? "text.disabled" : "text.primary"
                                }
                              >
                                {item?.label}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Form key: {item?.name}
                              </Typography>
                            }
                          />
                        </Box>
                        {isSelected && (
                          <Chip label="Selected" color="success" size="small" />
                        )}
                        {isDisabled && <Chip label="Required" size="small" />}
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "error.main",
                }}
              >
                <Typography variant="h6">
                  We do not have any forms available at this time.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
        <Box
          sx={{
            mt: 2,
            borderTop: "1px solid rgba(0,0,0,0.1)",
            pt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Selected documents: {newSelectedForms.length}
          </Typography>
          <LoadingButton
            onClick={handleAddingDoc}
            loading={addNewDocLoading}
            disabled={newSelectedForms.length === 0}
            color="success"
            variant="contained"
            sx={{ width: "12rem" }}
          >
            Submit Documents
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
}

export default React.memo(AddSinglePermitDocumentModal);
